/**
 * FolderCard Component
 * Individual folder/album card displaying cover thumbnail, name, count, and date
 * Shows folder info with hover effects matching PhotoCard conventions
 *
 * @module components/gallery/FolderCard
 */

import React, { useEffect } from 'react';
import { Album, User } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

interface FolderCardProps {
  album: Album;
  currentUser?: User | null;
  onClick: (album: Album) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Folder card component displaying an album as a folder tile
 * Shows cover photo thumbnail (or placeholder), name, photo count, and date
 *
 * @component
 * @example
 * <FolderCard
 *   album={album}
 *   currentUser={user}
 *   onClick={handleFolderClick}
 * />
 */
const FolderCard: React.FC<FolderCardProps> = ({
  album,
  onClick,
}) => {
  // Debug logging
  useEffect(() => {
    logger.debug(`[WALRUS] FolderCard: mounted`, {
      albumId: album.id,
      albumName: album.name,
      photoCount: album.photoCount,
      hasCover: !!album.coverPhotoUrl,
    });
  }, [album.id, album.name, album.photoCount, album.coverPhotoUrl]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleClick = () => {
    logger.debug(`[WALRUS] FolderCard: clicked`, { albumId: album.id, albumName: album.name });
    onClick(album);
  };

  return (
    <div
      className="group relative overflow-hidden rounded-lg bg-walrus-white border border-walrus-gray transition-all duration-200 hover:shadow-lg hover:border-walrus-black cursor-pointer"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Cover image container - aspect-video for wider folder appearance */}
      <div className="relative aspect-video overflow-hidden bg-walrus-cream">
        {album.coverPhotoUrl ? (
          <>
            <img
              src={album.coverPhotoUrl}
              alt={`Cover for ${album.name}`}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />
            {/* Gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          </>
        ) : (
          <>
            {/* Placeholder folder icon when no cover photo */}
            <div className="flex h-full w-full items-center justify-center bg-walrus-cream">
              <svg
                className="h-16 w-16 text-walrus-gray"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
            </div>
            {/* Gradient overlay even for placeholder */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </>
        )}

        {/* Photo count badge - top right */}
        <div className="absolute top-2 right-2 rounded-full bg-walrus-black/70 px-2.5 py-1 text-xs font-medium text-white">
          {album.photoCount} {album.photoCount === 1 ? 'photo' : 'photos'}
        </div>

        {/* Album name overlay - bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="truncate font-semibold text-white text-base" title={album.name}>
            {album.name}
          </h3>
          {album.description && (
            <p className="truncate text-white/80 text-xs mt-0.5">
              {album.description}
            </p>
          )}
        </div>
      </div>

      {/* Footer with date */}
      <div className="px-3 py-2 flex items-center justify-between">
        <p className="text-xs text-walrus-silver">
          {formatDate(album.createdAt)}
        </p>
        {/* TODO: Add album owner name display when available */}
        <svg
          className="h-4 w-4 text-walrus-silver transition-transform duration-200 group-hover:translate-x-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>
    </div>
  );
};

FolderCard.displayName = 'FolderCard';

export default FolderCard;
